/**
 * Payroll without claims — the people whose labor is in the org's cost per claim in a
 * month they billed no claims (Shayna, review #230, 2026-10-08).
 *
 * One row per person with any such month in the range: each claimless month's labor,
 * the total, and how many of their paid months billed claims and how many did not. A
 * biller can be on both sides — claims in March, none in April.
 *
 * The dollars are `claimLabor`, the figure cost per claim divides (benefit-loaded), not
 * FactPayroll pay. They are the person's `unattributedLabor` month by month, so the
 * rows add up to the Overview's unattributed labor for the same filters, to the cent
 * (MEASURE-RULES §3.5). Read `Person.months`, which a claim filter has already scaled,
 * so that holds under the claim filters too.
 */
import type { Person } from '@/data/selectors'
import { isNoClaimMonth } from '@/data/hydrate'

export interface NoClaimPerson {
  person: Person
  /** Month → the month's labor, for the months in range that billed no claims. */
  withoutClaims: Map<string, number>
  /** The months in range the person billed claims. Their cells are blank. */
  withClaims: Set<string>
  /** Sum of `withoutClaims`: the person's unattributed labor over the range. */
  total: number
}

export interface PayrollWithoutClaims {
  /** Largest total first. */
  rows: NoClaimPerson[]
  /** Month → the column's total, over every row. */
  byMonth: Map<string, number>
  total: number
}

/** The people in `people` with a claimless paid month in their (range-trimmed) months. */
export function payrollWithoutClaims(people: readonly Person[]): PayrollWithoutClaims {
  const rows: NoClaimPerson[] = []
  const byMonth = new Map<string, number>()
  let total = 0
  for (const p of people) {
    const withoutClaims = new Map<string, number>()
    const withClaims = new Set<string>()
    for (const m of p.months) {
      if (isNoClaimMonth(m)) withoutClaims.set(m.month, (withoutClaims.get(m.month) ?? 0) + m.claimLabor)
      else withClaims.add(m.month)
    }
    // A claim filter can scale a person's claimless months to nothing; nothing to list.
    if ([...withoutClaims.values()].every((v) => v === 0)) continue
    let own = 0
    for (const [month, v] of withoutClaims) {
      own += v
      byMonth.set(month, (byMonth.get(month) ?? 0) + v)
    }
    total += own
    rows.push({ person: p, withoutClaims, withClaims, total: own })
  }
  rows.sort((a, b) => b.total - a.total || a.person.employee.localeCompare(b.person.employee))
  return { rows, byMonth, total }
}

export type GroupBy = 'none' | 'department' | 'supervisor'

export interface NoClaimGroup {
  /** The department, or the direct supervisor's name. */
  key: string
  rows: NoClaimPerson[]
  /** Month → the group's total. */
  byMonth: Map<string, number>
  total: number
  /** Person-months without claims, and with them, over the group. */
  monthsWithout: number
  monthsWith: number
}

/** The direct supervisor, as the Supervisors page reads the line. */
export const NO_SUPERVISOR = 'No supervisor'
export const SUPERVISOR_OUTSIDE = 'Supervisor outside your access'

export function groupKey(p: Person, by: Exclude<GroupBy, 'none'>): string {
  if (by === 'department') return p.department || 'Unknown'
  if (p.supervisor) return p.supervisor
  return p.supervisorOutsideScope ? SUPERVISOR_OUTSIDE : NO_SUPERVISOR
}

/**
 * The rows under one header per department (home department) or direct supervisor.
 * Groups come largest total first; each group's rows keep the order they came in.
 * The group totals add up to the rows' — nothing is counted twice or dropped.
 */
export function groupRows(rows: readonly NoClaimPerson[], by: Exclude<GroupBy, 'none'>): NoClaimGroup[] {
  const groups = new Map<string, NoClaimGroup>()
  for (const r of rows) {
    const key = groupKey(r.person, by)
    let g = groups.get(key)
    if (!g) {
      g = { key, rows: [], byMonth: new Map(), total: 0, monthsWithout: 0, monthsWith: 0 }
      groups.set(key, g)
    }
    g.rows.push(r)
    g.total += r.total
    g.monthsWithout += r.withoutClaims.size
    g.monthsWith += r.withClaims.size
    for (const [m, v] of r.withoutClaims) g.byMonth.set(m, (g.byMonth.get(m) ?? 0) + v)
  }
  return [...groups.values()].sort((a, b) => b.total - a.total || a.key.localeCompare(b.key))
}
